import axios from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';

import Button from 'components/button';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import Header from 'components/text/header';
import UseApi from 'hooks/useApi';
import useForm from 'hooks/useForm';
import { useParams } from 'react-router-dom';
import ImageUpload from 'components/imageUpload';

const initialValues = {
  package: '',
  user: '',
  state: 'pending',
  'persons[0].firstName': '',
  'persons[0].lastName': '',
  'persons[0].cnic': '',
  'persons[0].passport': '',
  'persons[0].cnicImageFront': '',
  'persons[0].cnicImageBack': '',
  'persons[0].passportImage1': '',
  'persons[0].passportImage2': '',
  'persons[0].ageGroup': 'Adult',
  'persons[0].roomType': 'Double',
  'billing.bookingAmount': '',
  'billing.VAT': '',
  'billing.discount': '',
  'billing.totalAmount': '',
  'billing.firstName': '',
  'billing.lastName': '',
  'billing.email': '',
  'billing.phone': '',
  'billing.address': '',
  'billing.city': '',
  'billing.zip': '',
  'billing.comments': '',
  'billing.paymentMethod': 'Bank',
  'billing.coupon': '',
  'billing.receipt': '',
};

interface IProps {
  formValues?: any;
  type: 'Create' | 'Update';
  onSubmit?: () => void;
  handleToggleCreatePackage?: () => void;
}

function BookingForm({ formValues, type, onSubmit, handleToggleCreatePackage }: IProps) {
  const { patchApi, postApi, getApi } = UseApi();
  const { id } = useParams<{ id: string }>();
  const [packages, setPackages] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'basic' | 'persons' | 'billing'>('basic');
  const [personCount, setPersonCount] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [packagesRes, usersRes] = await Promise.all([
          getApi('packages'),
          getApi('users'),
        ]);

        if (packagesRes?.data) {
          setPackages(packagesRes.data.data.packages);
        }

        if (usersRes?.data) {
          setUsers(usersRes.data.data.users);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  function prepareFormData() {
    const fieldNames = Object.keys(values);

    const formData: any = {
      package: values.package,
      user: values.user,
      state: values.state,
      persons: [],
      billing: {},
    };

    for (let i = 0; i < personCount; i++) {
      const person: any = {};

      fieldNames.forEach((key) => {
        const match = key.match(new RegExp('^persons\\[' + i + '\\]\\.(.+)$'));
        if (match) {
          const fieldName = match[1];
          let fieldValue = values[key];
          
          if (
            ['cnicImageFront', 'cnicImageBack', 'passportImage1', 'passportImage2'].includes(fieldName) &&
            typeof fieldValue === 'object' &&
            fieldValue !== null
          ) {
            if (fieldValue && Object.prototype.hasOwnProperty.call(fieldValue, 'data')) {
              fieldValue = (fieldValue as any).data;
            }
          }
          
          person[fieldName] = fieldValue;
        }
      });

      if (Object.keys(person).length > 0) {
        formData.persons.push(person);
      }
    }

    fieldNames.forEach((key) => {
      const match = key.match(/^billing\.(.+)$/);
      if (match) {
        const fieldName = match[1];
        let fieldValue = values[key];

        if (fieldName === 'receipt' && typeof fieldValue === 'object' && fieldValue !== null) {
          if (fieldValue && Object.prototype.hasOwnProperty.call(fieldValue, 'data')) {
            fieldValue = (fieldValue as any).data;
          }
        }

        if (['bookingAmount', 'VAT', 'discount', 'totalAmount'].includes(fieldName)) {
          formData.billing[fieldName] = fieldValue ? Number(fieldValue) : 0;
        } else {
          formData.billing[fieldName] = fieldValue;
        }
      }
    });

    return formData;
  }

  function customHandleChange(e: any) {
    const { name, value } = e.target;
    
    if (name.includes('cnicImageFront') || 
        name.includes('cnicImageBack') || 
        name.includes('passportImage1') || 
        name.includes('passportImage2') || 
        name.includes('receipt')) {
      
      let processedValue = value;
      
      if (typeof value === 'object' && value !== null && 
          Object.prototype.hasOwnProperty.call(value, 'message') && 
          value.message === 'Success' && 
          Object.prototype.hasOwnProperty.call(value, 'data')) {
        processedValue = (value as any).data;
      }
      
      const syntheticEvent = {
        target: {
          name: name,
          value: processedValue
        }
      } as ChangeEvent<HTMLInputElement>;
      
      handleChange(syntheticEvent);
    } else {
      handleChange(e);
    }
  }

  const createBooking = async () => {
    const formattedValues = prepareFormData();

    let res;
    if (type === 'Update' && id) {
      res = await patchApi('bookings/' + id, formattedValues);
    } else {
      res = await postApi('bookings', formattedValues);
    }

    if (res?.data.message) {
      resetForm();
      onSubmit?.();
      handleToggleCreatePackage?.();
    }
  };

  function prepareInitialValues(doc: any): any {
    const flatValues: any = {
      package: doc.package || '',
      user: doc.user || '',
      state: doc.state || 'pending',
    };

    if (doc.persons && Array.isArray(doc.persons)) {
      setPersonCount(doc.persons.length);

      doc.persons.forEach((person: any, index: number) => {
        Object.entries(person).forEach(([key, value]) => {
          flatValues['persons[' + index + '].' + key] = value || '';
        });
      });
    }

    if (doc.billing) {
      Object.entries(doc.billing).forEach(([key, value]) => {
        flatValues['billing.' + key] = value || '';
      });
    }

    return flatValues;
  }

  const { values, handleChange, resetForm, handleSubmit } = useForm(
    formValues ? prepareInitialValues(formValues) : initialValues,
    createBooking,
    [
      'package',
      'user',
      'state',
      'billing.bookingAmount',
      'billing.VAT',
      'billing.totalAmount',
      'billing.firstName',
      'billing.lastName',
      'billing.email',
      'billing.phone',
      'billing.paymentMethod'
    ]
  );

  const addPerson = () => {
    setPersonCount((prev) => prev + 1);
  };

  const removePerson = (index: number) => {
    if (personCount <= 1) return;

    const newValues = { ...values };

    Object.keys(newValues).forEach((key) => {
      if (key.startsWith('persons[' + index + ']')) {
        delete newValues[key];
      }
    });

    for (let i = index + 1; i < personCount; i++) {
      Object.keys(newValues).forEach((key) => {
        const match = key.match(new RegExp('^persons\\[' + i + '\\]\\.(.+)$'));
        if (match) {
          const fieldName = match[1];
          newValues['persons[' + (i - 1) + '].' + fieldName] = newValues[key];
          delete newValues[key];
        }
      });
    }

    setPersonCount((prev) => prev - 1);
  };

  const nextTab = () => {
    if (activeTab === 'basic') {
      setActiveTab('persons');
    } else if (activeTab === 'persons') {
      setActiveTab('billing');
    }
  };

  const prevTab = () => {
    if (activeTab === 'billing') {
      setActiveTab('persons');
    } else if (activeTab === 'persons') {
      setActiveTab('basic');
    }
  };

  const renderPersonForm = (index: number) => {
    return (
      <div key={index} className="mb-4 rounded border p-4">
        <div className="mb-3 flex justify-between">
          <h3 className="text-lg font-medium">Person {index + 1}</h3>
          {personCount > 1 && (
            <button
              type="button"
              className="text-red-500"
              onClick={() => removePerson(index)}
            >
              Remove
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <InputField
            variant="auth"
            label="First Name*"
            placeholder="Enter first name"
            id={"person-" + index + "-firstName"}
            type="text"
            value={values['persons[' + index + '].firstName'] || ''}
            onChange={handleChange}
            name={'persons[' + index + '].firstName'}
          />

          <InputField
            variant="auth"
            label="Last Name*"
            placeholder="Enter last name"
            id={"person-" + index + "-lastName"}
            type="text"
            value={values['persons[' + index + '].lastName'] || ''}
            onChange={handleChange}
            name={'persons[' + index + '].lastName'}
          />

          <InputField
            variant="auth"
            label="CNIC"
            placeholder="Enter CNIC"
            id={"person-" + index + "-cnic"}
            type="text"
            value={values['persons[' + index + '].cnic'] || ''}
            onChange={handleChange}
            name={'persons[' + index + '].cnic'}
          />

          <InputField
            variant="auth"
            label="Passport"
            placeholder="Enter passport"
            id={"person-" + index + "-passport"}
            type="text"
            value={values['persons[' + index + '].passport'] || ''}
            onChange={handleChange}
            name={'persons[' + index + '].passport'}
          />

          <div className="col-span-1">
            <ImageUpload
              id={"person-" + index + "-cnicImageFront"}
              name={'persons[' + index + '].cnicImageFront'}
              label="CNIC Image Front"
              value={values['persons[' + index + '].cnicImageFront'] || ''}
              onChange={customHandleChange}
              error={null}
              folder="bookings"
            />
          </div>

          <div className="col-span-1">
            <ImageUpload
              id={"person-" + index + "-cnicImageBack"}
              name={'persons[' + index + '].cnicImageBack'}
              label="CNIC Image Back"
              value={values['persons[' + index + '].cnicImageBack'] || ''}
              onChange={customHandleChange}
              error={null}
              folder="bookings"
            />
          </div>

          <div className="col-span-1">
            <ImageUpload
              id={"person-" + index + "-passportImage1"}
              name={'persons[' + index + '].passportImage1'}
              label="Passport Image 1"
              value={values['persons[' + index + '].passportImage1'] || ''}
              onChange={customHandleChange}
              error={null}
              folder="bookings"
            />
          </div>

          <div className="col-span-1">
            <ImageUpload
              id={"person-" + index + "-passportImage2"}
              name={'persons[' + index + '].passportImage2'}
              label="Passport Image 2"
              value={values['persons[' + index + '].passportImage2'] || ''}
              onChange={customHandleChange}
              error={null}
              folder="bookings"
            />
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Age Group
            </label>
            <select
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
              value={values['persons[' + index + '].ageGroup'] || 'Adult'}
              onChange={(e) => {
                const syntheticEvent = {
                  target: {
                    name: 'persons[' + index + '].ageGroup',
                    value: e.target.value,
                  },
                } as unknown as ChangeEvent<HTMLInputElement>;
                handleChange(syntheticEvent);
              }}
            >
              <option value="Adult">Adult</option>
              <option value="Child">Child</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Room Type
            </label>
            <select
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
              value={values['persons[' + index + '].roomType'] || 'Double'}
              onChange={(e) => {
                const syntheticEvent = {
                  target: {
                    name: 'persons[' + index + '].roomType',
                    value: e.target.value,
                  },
                } as unknown as ChangeEvent<HTMLInputElement>;
                handleChange(syntheticEvent);
              }}
            >
              <option value="Double">Double</option>
              <option value="Triple">Triple</option>
              <option value="Quad">Quad</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  const validateBasicInfo = () => {
    return values.package && values.user;
  };

  const validatePersons = () => {
    for (let i = 0; i < personCount; i++) {
      if (
        !values['persons[' + i + '].firstName'] ||
        !values['persons[' + i + '].lastName']
      ) {
        return false;
      }
    }
    return true;
  };

  return (
    <Card extra="p-4 mb-4">
      <Header>{type} Booking</Header>

      <div className="mb-4 flex border-b">
        <button
          className={"px-4 py-2 " + 
            (activeTab === 'basic' ? 'border-b-2 border-blue-500' : '')
          }
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button
          className={"px-4 py-2 " + 
            (activeTab === 'persons' ? 'border-b-2 border-blue-500' : '')
          }
          onClick={() => setActiveTab('persons')}
        >
          Persons
        </button>
        <button
          className={"px-4 py-2 " + 
            (activeTab === 'billing' ? 'border-b-2 border-blue-500' : '')
          }
          onClick={() => setActiveTab('billing')}
        >
          Billing
        </button>
      </div>

      {activeTab === 'basic' && (
        <>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Package*
            </label>
            <select
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
              value={values.package}
              onChange={(e) => {
                const syntheticEvent = {
                  target: {
                    name: 'package',
                    value: e.target.value,
                  },
                } as unknown as ChangeEvent<HTMLInputElement>;
                handleChange(syntheticEvent);
              }}
            >
              <option value="">Select Package</option>
              {packages.map((pkg: any) => (
                <option key={pkg._id} value={pkg._id}>
                  {pkg.packageName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              User*
            </label>
            <select
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
              value={values.user}
              onChange={(e) => {
                const syntheticEvent = {
                  target: {
                    name: 'user',
                    value: e.target.value,
                  },
                } as unknown as ChangeEvent<HTMLInputElement>;
                handleChange(syntheticEvent);
              }}
            >
              <option value="">Select User</option>
              {users.map((user: any) => (
                <option key={user._id} value={user._id}>
                  {user.username || user.email}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Booking State
            </label>
            <select
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
              value={values.state}
              onChange={(e) => {
                const syntheticEvent = {
                  target: {
                    name: 'state',
                    value: e.target.value,
                  },
                } as unknown as ChangeEvent<HTMLInputElement>;
                handleChange(syntheticEvent);
              }}
            >
              <option value="pending">Pending</option>
              <option value="successful">Successful</option>
              <option value="unsuccessful">Unsuccessful</option>
            </select>
          </div>

          <div className="mt-6 flex justify-end">
            <Button extraClasses="px-6" onClick={nextTab}>
              Next
            </Button>
          </div>
        </>
      )}

      {activeTab === 'persons' && (
        <>
          {Array.from({ length: personCount }).map((_, index) =>
            renderPersonForm(index),
          )}

          <Button extraClasses="mb-4" onClick={addPerson} variant="default">
            Add Person
          </Button>

          <div className="mt-6 flex justify-between">
            <Button extraClasses="px-6" onClick={prevTab} variant="gray">
              Back
            </Button>
            <Button extraClasses="px-6" onClick={nextTab}>
              Next
            </Button>
          </div>
        </>
      )}

      {activeTab === 'billing' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <InputField
              variant="auth"
              label="Booking Amount*"
              placeholder="Enter booking amount"
              id="billing-bookingAmount"
              type="number"
              value={values['billing.bookingAmount']}
              onChange={handleChange}
              name="billing.bookingAmount"
            />

            <InputField
              variant="auth"
              label="VAT*"
              placeholder="Enter VAT"
              id="billing-VAT"
              type="number"
              value={values['billing.VAT']}
              onChange={handleChange}
              name="billing.VAT"
            />

            <InputField
              variant="auth"
              label="Discount"
              placeholder="Enter discount"
              id="billing-discount"
              type="number"
              value={values['billing.discount']}
              onChange={handleChange}
              name="billing.discount"
            />

            <InputField
              variant="auth"
              label="Total Amount*"
              placeholder="Enter total amount"
              id="billing-totalAmount"
              type="number"
              value={values['billing.totalAmount']}
              onChange={handleChange}
              name="billing.totalAmount"
            />

            <InputField
              variant="auth"
              label="First Name*"
              placeholder="Enter first name"
              id="billing-firstName"
              type="text"
              value={values['billing.firstName']}
              onChange={handleChange}
              name="billing.firstName"
            />

            <InputField
              variant="auth"
              label="Last Name*"
              placeholder="Enter last name"
              id="billing-lastName"
              type="text"
              value={values['billing.lastName']}
              onChange={handleChange}
              name="billing.lastName"
            />

            <InputField
              variant="auth"
              label="Email*"
              placeholder="Enter email"
              id="billing-email"
              type="email"
              value={values['billing.email']}
              onChange={handleChange}
              name="billing.email"
            />

            <InputField
              variant="auth"
              label="Phone*"
              placeholder="Enter phone"
              id="billing-phone"
              type="text"
              value={values['billing.phone']}
              onChange={handleChange}
              name="billing.phone"
            />

            <InputField
              variant="auth"
              label="Address"
              placeholder="Enter address"
              id="billing-address"
              type="text"
              value={values['billing.address']}
              onChange={handleChange}
              name="billing.address"
            />

            <InputField
              variant="auth"
              label="City"
              placeholder="Enter city"
              id="billing-city"
              type="text"
              value={values['billing.city']}
              onChange={handleChange}
              name="billing.city"
            />

            <InputField
              variant="auth"
              label="ZIP"
              placeholder="Enter ZIP code"
              id="billing-zip"
              type="text"
              value={values['billing.zip']}
              onChange={handleChange}
              name="billing.zip"
            />

            <InputField
              variant="auth"
              label="Coupon ID"
              placeholder="Enter coupon ID"
              id="billing-coupon"
              type="text"
              value={values['billing.coupon']}
              onChange={handleChange}
              name="billing.coupon"
            />

            <div className="col-span-2">
              <ImageUpload
                id="billing-receipt"
                name="billing.receipt"
                label="Payment Receipt"
                value={values['billing.receipt'] || ''}
                onChange={customHandleChange}
                error={null}
                folder="receipts"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Comments
              </label>
              <textarea
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                rows={3}
                value={values['billing.comments']}
                onChange={(e) => {
                  const syntheticEvent = {
                    target: {
                      name: 'billing.comments',
                      value: e.target.value,
                    },
                  } as unknown as ChangeEvent<HTMLInputElement>;
                  handleChange(syntheticEvent);
                }}
                name="billing.comments"
                placeholder="Enter comments"
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Payment Method
              </label>
              <select
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                value={values['billing.paymentMethod']}
                onChange={(e) => {
                  const syntheticEvent = {
                    target: {
                      name: 'billing.paymentMethod',
                      value: e.target.value,
                    },
                  } as unknown as ChangeEvent<HTMLInputElement>;
                  handleChange(syntheticEvent);
                }}
              >
                <option value="Bank">Bank</option>
                <option value="Card">Card</option>
                <option value="InPerson">In Person</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <Button extraClasses="px-6" onClick={prevTab} variant="gray">
              Back
            </Button>
            <Button extraClasses="px-6" onClick={handleSubmit}>
              {type}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export default BookingForm;